import {
    getAddresses,
    getEmployees,
    getEquipments,
    getLocations,
    getMaterials,
    getProfessions,
    getProjects,
    getProjectsWithTask,
    getPurchaseOrders,
    getSuppliers,
    getTasks
} from '@/api/resources'
import SearchableSelect from '@/components/forms/SearchableSelect'
import { useEffect, useState } from 'preact/hooks'
import { useQuery } from '@tanstack/react-query'
import { isQueryLoading } from '@/helpers'
import { Api } from '@/types'
import { JSX } from 'preact/jsx-runtime'
import { FC } from 'preact/compat'

type Props<T> = {
    name: string
    label: string
    isLoading: boolean
    resource: T[] | undefined
    selectedId?: number | undefined
    onMatch?: (match: T | undefined) => void
} & JSX.HTMLAttributes<HTMLInputElement>

export const FuzzySelect = <T extends Api.Resource,>({ name, label, isLoading, resource, ...p }: Props<T>) => {
    const [selected, setSelected] = useState<T>()

    useEffect(() => {

        if (p.defaultValue) {
            const val = resource?.find(item => item.id === Number.parseInt(p.defaultValue as string))
            setSelected(val)
            if (val && p.onMatch) p.onMatch(val)
        } else if (p.selectedId) {
            const val = resource?.find(item => item.id === Number(p.selectedId))
            setSelected(val)
        } else {
            setSelected(undefined)
        }
    }, [resource, p.selectedId, p.defaultValue])

    const handleChange = (option: { id: number | string; name: string } | undefined) => {
        if (!option) {
            setSelected(undefined)
            p.onMatch?.(undefined)
            return
        }
        const match = resource?.find(item => item.id === option.id) as T | undefined
        setSelected(match)
        p.onMatch?.(match)
    }

    const options = (resource ?? []).map(item => ({
        id: item.id,
        name: item.name,
    }))

    return (
        <SearchableSelect
            label={label}
            name={name}
            options={options}
            value={selected ? { id: selected.id, name: selected.name } : undefined}
            onChange={handleChange}
            isLoading={isLoading}
            disabled={p.disabled ?? resource?.length === 0}
            required={p.required}
            className={p.className}
        />
    )
}

type MakeFuzzySelectProps = {
    name: string
    label: string
    onMatch?: (match: object | undefined) => void
} & JSX.HTMLAttributes<HTMLInputElement>

function makeFuzzySelect<T extends Api.Resource>(getResource: () => Promise<T[]>, resourceName: string, filter?: (items: T[]) => T[]): FC<MakeFuzzySelectProps> {
    return ({ name, label, ...p }) => {
        const { status: status, fetchStatus: fetchStatus, data: resource, error: error } = useQuery<T[]>({
            queryKey: [resourceName],
            queryFn: getResource,
            select: filter
        })
        return (
            <FuzzySelect
                name={name}
                label={label}
                isLoading={isQueryLoading(status, fetchStatus)}
                resource={resource}
                {...p}
            />
        )
    }
}

export const TaskSelect = makeFuzzySelect<Api.Task>(getTasks, 'tasks')
export const TaskForProjectSelect = makeFuzzySelect<Api.Task>(getTasks, 'tasks')
export const ProjectSelect = makeFuzzySelect<Api.Project>(getProjects, 'projects')
export const ProjectWithTaskSelect = makeFuzzySelect<Api.Project>(getProjectsWithTask, 'projects-tasks')
export const EmployeeSelect = makeFuzzySelect<Api.Employee>(getEmployees, 'employees')
export const EquipmentSelect = makeFuzzySelect<Api.Equipment>(getEquipments, 'equipments')
export const DrillSelect = makeFuzzySelect<Api.Equipment>(getEquipments, 'equipments', (items) => items.filter(i => i.status.slug === 'drilling-machine' || i.status.slug === 'air-drill'))
export const MaterialSelect = makeFuzzySelect<Api.Material>(getMaterials, 'materials')
export const LocationSelect = makeFuzzySelect<Api.Resource>(getLocations, 'locations')
export const PurchaseOrderSelect = makeFuzzySelect<Api.PurchaseOrder>(getPurchaseOrders, 'purchase-orders')
export const ProfessionSelect = makeFuzzySelect<Api.Profession>(getProfessions, 'professions')
export const ClientSelect = makeFuzzySelect<Api.Supplier>(getSuppliers, 'suppliers')
export const AddressSelect = makeFuzzySelect<Api.Address>(getAddresses, 'addresses')
export const BorerSelect = makeFuzzySelect<Api.Equipment>(getEquipments, 'equipments', (items) => items.filter(i => i.status.slug === 'borer'))
export const TruckSelect = makeFuzzySelect<Api.Equipment>(getEquipments, 'equipments', (items) => items.filter(i => i.status.slug === 'truck'))
export const CompressorSelect = makeFuzzySelect<Api.Equipment>(getEquipments, 'equipments', (items) => items.filter(i => i.status.slug === 'compressor'))
export const RelatedEquipmentSelect = makeFuzzySelect<Api.Equipment>(getEquipments, 'equipments', (items) => items.filter(i => i.is_maintenance))
